import React from 'react';

import { Chip, Stack, Tooltip, Typography } from '@mui/material';

import { type AiCodeKind } from '~/services/PromoVoucherAiService';

type Props = {
  kind: AiCodeKind;
  onSelect: (query: string) => void;
  disabled?: boolean;
};

const queryExamples = {
  promo: [
    {
      label: 'Percentage discount',
      query:
        'Give 10% off hotel bookings in Australia, capped at AUD 100 per booking. Require a minimum spend of AUD 500, allow one use per customer, and expire 30 days from today.',
    },
    {
      label: 'Fixed discount',
      query:
        'Give AUD 50 off hotel bookings in Australia with a minimum spend of AUD 500. Limit the promo to 100 redemptions in total and one use per customer, and expire 30 days from today.',
    },
    {
      label: 'Extend expiry',
      query:
        'Extend the expiry date to 30 days from today. Keep all existing discounts, eligibility rules and usage limits unchanged.',
    },
  ],
  voucher: [
    {
      label: 'Hotel reward',
      query:
        'Create a voucher offering AUD 100 off hotel bookings in Australia with a minimum spend of AUD 1,000. Allow one redemption per assigned user and expire 30 days from today.',
    },
    {
      label: 'Experience reward',
      query:
        'Create a voucher offering AUD 25 off experiences in Australia with a minimum spend of AUD 150. Allow one redemption per assigned user and expire 30 days from today.',
    },
    {
      label: 'Extend expiry',
      query:
        'Extend the expiry date to 30 days from today. Keep all existing rewards, eligibility rules and usage limits unchanged.',
    },
  ],
} as const;

export default function PromoAiQueryExamples({ kind, onSelect, disabled = false }: Props) {
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        Try an example, then edit the query to suit your requirements.
      </Typography>
      <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
        {queryExamples[kind].map((example) => (
          <Tooltip key={example.label} title={example.query}>
            <Chip
              label={example.label}
              aria-label={`Use example: ${example.query}`}
              variant="outlined"
              size="small"
              disabled={disabled}
              onClick={() => onSelect(example.query)}
            />
          </Tooltip>
        ))}
      </Stack>
    </Stack>
  );
}
